import * as React from 'react'
import { X } from '@phosphor-icons/react'
import { OverlayWrapper, Backdrop } from '../core/overlay'
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogClose,
} from '../core/dialog'
import { Button } from '../core/button'
import {
  SortableList,
  SortableListItem,
  SortableRowButton,
} from '../core/sortable-list'
import {
  EntityGlyph,
  type EntityRef,
  entityTypeConfig,
} from '../core/entity-glyph'

/**
 * EditPinsModal — the "Edit pins" surface behind the homepage `PinnedWidget`’s
 * ⋯ menu, as a transactional `Dialog`: drag to reorder pins or remove them, then
 * commit with "Apply changes" (or discard with Cancel). Like
 * {@link OrganizeStagesModal}, edits live in draft state and nothing changes
 * behind the modal until Save — so a stray backdrop click can't drop the order.
 *
 *   <EditPinsModal
 *     trigger={<Button>Edit pins</Button>}
 *     items={pins}
 *     onApply={(next) => savePinOrder(next)}
 *   />
 *
 * Reuses the shared {@link SortableList} primitive (the same core component
 * behind the Columns Panel and Organize Stages), with an {@link EntityGlyph}
 * leading each row and a remove × trailing it.
 */

export interface EditPinsModalProps {
  /** Element that opens the modal — its `onClick` is wired through. */
  trigger: React.ReactElement
  /** Pinned records, in current order. */
  items: EntityRef[]
  /** Called on Apply with the committed order (removed pins dropped). */
  onApply: (items: EntityRef[]) => void
  /** Dialog heading. */
  title?: React.ReactNode
  /** Copy under the title. */
  description?: React.ReactNode
  /** Controlled open state. */
  isOpen?: boolean
  /** Uncontrolled initial open state. */
  defaultOpen?: boolean
  /** Called when the open state changes. */
  onOpenChange?: (isOpen: boolean) => void
}

export function EditPinsModal({
  trigger,
  items,
  onApply,
  title = 'Edit pins',
  description = 'Drag to reorder, or remove pins from your homepage.',
  isOpen,
  defaultOpen,
  onOpenChange,
}: EditPinsModalProps) {
  return (
    <OverlayWrapper
      trigger={trigger}
      isOpen={isOpen}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
    >
      {/* Transactional: not dismissable on backdrop/Escape — commit with Apply
          or discard with Cancel, so a stray click can't drop the new order. */}
      <Backdrop className="max-w-lg" isDismissable={false}>
        <Dialog>
          {({ close }) => (
            // Mounts fresh each open (Backdrop unmounts children on close), so
            // the draft re-seeds from the latest `items` every time.
            <EditPinsContent
              close={close}
              items={items}
              onApply={onApply}
              title={title}
              description={description}
            />
          )}
        </Dialog>
      </Backdrop>
    </OverlayWrapper>
  )
}
EditPinsModal.displayName = 'EditPinsModal'

interface EditPinsContentProps {
  close: () => void
  items: EntityRef[]
  onApply: (items: EntityRef[]) => void
  title: React.ReactNode
  description: React.ReactNode
}

function EditPinsContent({
  close,
  items,
  onApply,
  title,
  description,
}: EditPinsContentProps) {
  // Draft order — seeded on mount (open) and mutated locally until Apply commits
  // or Cancel/close discards.
  const [order, setOrder] = React.useState<EntityRef[]>(() => items.slice())

  const removePin = (id: string) =>
    setOrder((prev) => prev.filter((p) => p.id !== id))

  const commit = () => {
    onApply(order)
    close()
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>

      <DialogBody>
        <SortableList
          items={order}
          getItemId={(p) => p.id}
          onReorder={setOrder}
          aria-label="Pinned records"
          renderEmptyState={() => (
            <p className="rounded-lg border border-dashed border-fbui-gray-200 py-8 text-center text-sm text-fbui-gray-500">
              No pins. Your homepage strip will be empty.
            </p>
          )}
        >
          {(pin) => (
            <SortableListItem
              textValue={pin.name}
              // Bordered white card (not the default sage fill) to match the
              // Edit-pins spec; twMerge lets bg/border override the base.
              className="border border-fbui-gray-200 bg-fbui-base-white py-2 pr-2 hover:border-fbui-gray-300"
              leading={<EntityGlyph type={pin.type} size="sm" />}
              actions={
                <SortableRowButton
                  aria-label={`Remove ${pin.name}`}
                  onClick={() => removePin(pin.id)}
                  destructive
                >
                  <X />
                </SortableRowButton>
              }
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-medium text-fbui-gray-900">
                  {pin.name}
                </span>
                <span className="truncate text-xs font-medium uppercase tracking-wide text-fbui-gray-400">
                  {entityTypeConfig[pin.type].label}
                </span>
              </span>
            </SortableListItem>
          )}
        </SortableList>
      </DialogBody>

      <DialogFooter>
        <DialogClose appearance="outline" size="lg" className="flex-1">
          Cancel
        </DialogClose>
        <Button
          type="button"
          variant="primary"
          size="lg"
          className="flex-1"
          onClick={commit}
        >
          Apply changes
        </Button>
      </DialogFooter>
    </>
  )
}
